import { useEffect, useState } from 'react';

const COMPANY_LOGO = '/upload/getbyfilename/logo.png';

/**
 * Fired when the tenant's logo is uploaded or deleted. The chrome is mounted in
 * the layout, above the settings screen that changes the logo, so it can't be
 * told through props — it listens for this instead. Same pattern as the
 * `open-mobile-search` event the header already uses.
 */
const COMPANY_LOGO_CHANGED = 'company-logo-changed';

/** Call after a successful logo upload or delete so mounted chrome re-probes. */
export const notifyCompanyLogoChanged = (): void => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(COMPANY_LOGO_CHANGED));
};

interface CompanyLogo {
  /** Cache-busted logo URL. Empty until the probe resolves. */
  logoUrl: string;
  /** False when the tenant has no uploaded logo. */
  showCompanyLogo: boolean;
  /**
   * Pass to the <img> `onError`. The HEAD probe can pass while the GET still
   * fails (the file is replaced or unreadable), so the fallback needs a second
   * chance to kick in.
   */
  hideCompanyLogo: () => void;
}

/**
 * Whether the tenant has uploaded its own logo, for the app chrome. Both shells
 * (the legacy Header and the kit AppShell's TopHeader) show it in place of the
 * client name, so the probe lives here rather than in each of them.
 *
 * Re-probes on `notifyCompanyLogoChanged()`, with a fresh cache-buster so the
 * <img> refetches rather than serving the previous logo from cache.
 *
 * The settings screen keeps its own copy of this state: it sets the new logo
 * optimistically from the upload response, which is what makes its own preview
 * instant.
 */
export function useCompanyLogo(): CompanyLogo {
  const [logoUrl, setLogoUrl] = useState<string>('');
  const [showCompanyLogo, setShowCompanyLogo] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;

    const checkLogo = async (): Promise<void> => {
      try {
        const url = `${COMPANY_LOGO}?v=${Date.now()}`;
        const response = await fetch(url, {
          method: 'HEAD',
          cache: 'no-store',
        });
        if (cancelled) return;
        if (response.ok) {
          setLogoUrl(url);
          setShowCompanyLogo(true);
        } else {
          setShowCompanyLogo(false);
        }
      } catch {
        if (!cancelled) setShowCompanyLogo(false);
      }
    };

    checkLogo();
    window.addEventListener(COMPANY_LOGO_CHANGED, checkLogo);
    return (): void => {
      cancelled = true;
      window.removeEventListener(COMPANY_LOGO_CHANGED, checkLogo);
    };
  }, []);

  return {
    logoUrl,
    showCompanyLogo,
    hideCompanyLogo: () => setShowCompanyLogo(false),
  };
}

export default useCompanyLogo;
